/**
 * TURN CC · 22 (Design refresh) — Month grid calendar.
 *
 * Visual overhaul:
 *  - Today: numéro dans un cercle rempli bg-blue-500/text-white + ring
 *    d'accompagnement autour de la case.
 *  - Weekend cells: fond très léger slate-50 (dark: white/[0.02]) pour
 *    créer un rythme visuel.
 *  - Header: titre `text-2xl`, boutons nav ghost icônes.
 *  - Weekday header: uppercase XS + tracking-wider + border-bottom fine.
 *  - Bandeau corporate: overlay au-dessus des chips.
 *
 * Logique inchangée : même grid, même bandes, même popover overflow.
 */
import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import CalendarEvent from "./CalendarEvent";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeekMonday(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return x;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildMonthGrid(year, monthIdx) {
  const first = new Date(year, monthIdx, 1);
  const gridStart = startOfWeekMonday(first);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function computeBands(events, gridStart, gridEnd) {
  const bands = [];
  for (const evt of events) {
    if (evt.type !== "corporate_event") continue;
    const s = new Date(evt.starts_at);
    const e = new Date(evt.ends_at);
    const clipStart = s < gridStart ? gridStart : s;
    const clipEnd = e > gridEnd ? gridEnd : e;
    if (clipEnd < gridStart || clipStart > gridEnd) continue;
    let curStart = new Date(clipStart);
    curStart.setHours(0, 0, 0, 0);
    while (curStart <= clipEnd) {
      const diffDays = Math.floor((curStart - gridStart) / 86400000);
      const rowIdx = Math.floor(diffDays / 7);
      const rowStartIdx = rowIdx * 7;
      const rowEndIdx = rowStartIdx + 6;
      const rowStart = new Date(gridStart);
      rowStart.setDate(gridStart.getDate() + rowStartIdx);
      const rowEnd = new Date(gridStart);
      rowEnd.setDate(gridStart.getDate() + rowEndIdx);
      const segStart = curStart;
      const segEnd = clipEnd < rowEnd ? clipEnd : rowEnd;
      const startCol = ((segStart - rowStart) / 86400000);
      const spanDays = Math.floor((segEnd - segStart) / 86400000) + 1;
      bands.push({
        event: evt,
        rowIdx,
        startCol: Math.floor(startCol),
        span: spanDays,
        isStart: sameDay(segStart, new Date(evt.starts_at)),
        isEnd: sameDay(segEnd, new Date(evt.ends_at)),
      });
      curStart = new Date(rowEnd);
      curStart.setDate(rowEnd.getDate() + 1);
      curStart.setHours(0, 0, 0, 0);
    }
  }
  return bands;
}

export default function CalendarMonth({
  year, monthIdx, onNav, onEventClick, events, isLoading,
}) {
  const today = useMemo(() => new Date(), []);
  const cells = useMemo(() => buildMonthGrid(year, monthIdx), [year, monthIdx]);
  const gridStart = cells[0];
  const gridEnd = cells[cells.length - 1];

  const byDate = useMemo(() => {
    const m = new Map();
    for (const evt of events || []) {
      if (evt.type === "corporate_event") continue;
      const key = evt.slot_date || (evt.starts_at ? evt.starts_at.slice(0, 10) : null);
      if (!key) continue;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(evt);
    }
    return m;
  }, [events]);

  const bands = useMemo(() => computeBands(events || [], gridStart, gridEnd),
                        [events, gridStart, gridEnd]);

  const monthLabel = new Date(year, monthIdx, 1).toLocaleDateString(undefined, {
    month: "long", year: "numeric",
  });

  const goToday = () => onNav({ year: today.getFullYear(), monthIdx: today.getMonth() });
  const goPrev = () => {
    let y = year, m = monthIdx - 1;
    if (m < 0) { m = 11; y -= 1; }
    onNav({ year: y, monthIdx: m });
  };
  const goNext = () => {
    let y = year, m = monthIdx + 1;
    if (m > 11) { m = 0; y += 1; }
    onNav({ year: y, monthIdx: m });
  };

  return (
    <div data-testid="calendar-month" className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            data-testid="calendar-prev-btn"
            size="icon" variant="ghost"
            onClick={goPrev}
            className="h-8 w-8 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100 capitalize min-w-[180px]">
            {monthLabel}
          </div>
          <Button
            data-testid="calendar-next-btn"
            size="icon" variant="ghost"
            onClick={goNext}
            className="h-8 w-8 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button
          data-testid="calendar-today-btn"
          size="sm" variant="outline"
          onClick={goToday}
          className="h-8 text-xs font-medium border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"
        >
          Today
        </Button>
      </div>

      {/* Weekday header row */}
      <div className="grid grid-cols-7 gap-1 border-b border-slate-200/60 dark:border-white/5 pb-1">
        {WEEKDAY_LABELS.map((w) => (
          <div
            key={w}
            className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider text-center py-1"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Grid — 6 rows × 7 columns */}
      <div className="grid grid-cols-7 gap-1 relative" data-testid="calendar-grid">
        {cells.map((d, idx) => {
          const inMonth = d.getMonth() === monthIdx;
          const isToday = sameDay(d, today);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const key = toYmd(d);
          const dayEvents = byDate.get(key) || [];
          const rowIdx = Math.floor(idx / 7);
          const rowHasBand = bands.some((b) => b.rowIdx === rowIdx);
          const visible = dayEvents.slice(0, 3);
          const overflow = dayEvents.length - visible.length;
          return (
            <div
              key={idx}
              data-testid={`calendar-cell-${key}`}
              className={
                "min-h-[104px] rounded-xl p-1.5 flex flex-col gap-1 relative " +
                "border transition-all duration-150 " +
                (inMonth
                  ? "border-slate-200 dark:border-white/5 "
                  : "border-transparent ") +
                (inMonth && isWeekend
                  ? "bg-slate-50/60 dark:bg-white/[0.02] "
                  : inMonth
                    ? "bg-white dark:bg-transparent "
                    : "") +
                "hover:bg-slate-50 dark:hover:bg-white/5 " +
                (inMonth
                  ? "hover:shadow-sm hover:border-slate-300 dark:hover:border-white/10 "
                  : "") +
                (isToday
                  ? "ring-2 ring-blue-500 ring-offset-1 ring-offset-white dark:ring-offset-slate-950 "
                  : "")
              }
            >
              {/* Day number */}
              <div className="flex items-center justify-between">
                {isToday ? (
                  <span
                    className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white text-xs font-semibold"
                    data-testid={`calendar-today-marker-${key}`}
                  >
                    {d.getDate()}
                  </span>
                ) : (
                  <span
                    className={
                      "text-sm font-semibold " +
                      (inMonth
                        ? "text-slate-700 dark:text-slate-200"
                        : "text-slate-400 dark:text-slate-600 opacity-50")
                    }
                  >
                    {d.getDate()}
                  </span>
                )}
              </div>

              {/* Reserve vertical space for the band overlay row. */}
              {rowHasBand && <div className="h-6 w-0" />}

              {/* Chips stack */}
              <div className="flex flex-col gap-1">
                {visible.map((e) => (
                  <CalendarEvent
                    key={e.id}
                    event={e}
                    onClick={onEventClick}
                  />
                ))}
                {overflow > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid={`calendar-cell-more-${key}`}
                        className="text-left text-[10px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 cursor-pointer px-1.5 py-0.5"
                      >
                        +{overflow} more
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="bottom" align="start"
                      className="w-[280px] bg-white dark:bg-slate-900 border-slate-200 dark:border-white/10 p-2 space-y-1"
                    >
                      <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 px-1 pb-1">
                        {d.toLocaleDateString(undefined, {
                          weekday: "long", day: "numeric", month: "long",
                        })}
                      </div>
                      {dayEvents.map((e) => (
                        <CalendarEvent key={e.id} event={e} onClick={onEventClick} />
                      ))}
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          );
        })}

        {/* Band overlay — one absolutely-positioned grid per row. */}
        <div className="absolute inset-0 pointer-events-none">
          {[0, 1, 2, 3, 4, 5].map((rowIdx) => {
            const rowBands = bands.filter((b) => b.rowIdx === rowIdx);
            if (rowBands.length === 0) return null;
            const rowTopPct = (rowIdx * 100) / 6;
            return (
              <div
                key={rowIdx}
                className="absolute left-0 right-0 grid grid-cols-7 gap-1 px-0"
                style={{ top: `calc(${rowTopPct}% + 30px)`, height: 24 }}
              >
                {rowBands.map((b, i) => (
                  <div
                    key={`${b.event.id}-${rowIdx}-${i}`}
                    className="pointer-events-auto"
                    style={{
                      gridColumn: `${b.startCol + 1} / span ${b.span}`,
                      height: 24,
                    }}
                  >
                    <CalendarEvent
                      event={b.event}
                      variant="band"
                      span={b.span}
                      isStart={b.isStart}
                      isEnd={b.isEnd}
                      onClick={onEventClick}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Fetching UI is handled by the parent module's header spinner. */}
    </div>
  );
}
